<template>
  <div
    class="relative flex h-full flex-col items-center justify-between space-y-3 px-5 py-6"
  >
    <IconBrandAlternateParentCompanyLogo
      v-if="showAlternateParentCompanyLogo"
      class="h-auto w-1/2"
    />
    <IconBrandParentCompanyLogo
      v-else
      class="h-auto w-[70%]"
    />
    <div class="grow self-stretch px-3 pb-4">
      <OnboardingStartScreen
        v-if="currentStageName === 'start'"
        @stage-complete="goToNextStage"
      />
      <OnboardingConfirmUserScreen
        v-if="currentStageName === 'confirm-user-identity'"
        @stage-complete="handleUserIsConfirmedComplete"
      />
      <OnboardingSetSupervisorPinScreen
        v-if="currentStageName === 'set-supervisor-pin'"
        @stage-complete="goToNextStage"
      />
      <OnboardingEndScreen
        v-if="currentStageName === 'end'"
        :merchant-name="merchantName"
        @stage-complete="clearTokens"
      />
    </div>
    <WizardProgressMeter
      :total-stage-count="totalStageCount"
      :current-stage-number="currentStageNumber"
    />
  </div>
</template>

<script setup lang="ts">
import {
  getOutstandingStages,
  type OnboardingStageName,
} from "@/services/onboarding/getOutstandingStages"
import * as Sentry from "@sentry/vue"

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { clearTokens } = useAuth()
const { isUKmerchant } = useTenant()

const merchantName = ref<string | null>(null) // Can't use useTenant as merchantName not returned until device is confirmed
const outstadingStageNames = ref<
  Array<OnboardingStageName>
>(["start", "end"])
const currentStageName = ref<OnboardingStageName>("start")

const currentStageNumber = computed<number>(
  () =>
    outstadingStageNames.value.indexOf(
      currentStageName.value
    ) + 1
)

const totalStageCount = computed<number>(
  () => outstadingStageNames.value.length
)

const handleUserIsConfirmedComplete = (
  name: string | null
) => {
  merchantName.value = name
  goToNextStage()
}

const goToNextStage = () => {
  currentStageName.value =
    outstadingStageNames.value[currentStageNumber.value]
}

onMounted(async () => {
  startLoading()
  try {
    const { stageNames } = await getOutstandingStages()
    outstadingStageNames.value = [
      "start",
      ...stageNames,
      "end",
    ]
  } catch (error) {
    Sentry.captureException(error)

    throw new UserVisibleError(
      "Error fetching onboarding status"
    )
  } finally {
    stopLoading()
  }
})

const showAlternateParentCompanyLogo = computed(
  () =>
    import.meta.env.VITE_ORGANIZATION === "boipa" &&
    isUKmerchant.value
)
</script>
